import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES, STORAGE_KEYS, RoleType } from '@/constants';
import { useAuthStore } from '@/stores';

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isLoading = useAuthStore(s => s.isLoading);

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export const GuestOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);

  // If the user has not been authenticated or their information is unavailable, allow children access.
  if (!isAuthenticated || !user) return <>{children}</>;

  const isSuperAdmin = user?.role === RoleType.SUPER_ADMIN;
  const hasSelectedMenuType = !!localStorage.getItem(STORAGE_KEYS.MENU_TYPE);

  if (isSuperAdmin && !hasSelectedMenuType) {
    return <Navigate to={ROUTES.ROLE_SELECTION_PAGE} replace />;
  }

  return <Navigate to={ROUTES.DASHBOARD} replace />;
};

/**
 * Guard: Require menu type selection for SUPER_ADMIN (role = 0)
 * - If user is SUPER_ADMIN and hasn't selected menuType yet
 * - Redirect to RoleSelectionPage to force selection
 */
export const RequireMenuTypeSelection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  const location = useLocation();

  // Check if user is SUPER_ADMIN (role = 0)
  const userRole = user?.role;
  const isSuperAdmin = userRole === RoleType.SUPER_ADMIN;

  // Check if menuType has been selected
  const menuType = localStorage.getItem(STORAGE_KEYS.MENU_TYPE);
  const hasSelectedMenuType = !!menuType;

  // If SUPER_ADMIN hasn't selected menuType yet, redirect to role selection page
  // But only if not already on the role selection page
  if (isSuperAdmin && !hasSelectedMenuType && location.pathname !== ROUTES.ROLE_SELECTION_PAGE) {
    return <Navigate to={ROUTES.ROLE_SELECTION_PAGE} replace />;
  }

  return <>{children}</>;
};
